import React, { useState } from "react";
import { User, MoreVertical, Bot, Loader2, FileText, ChevronDown, X } from "lucide-react";
import { generateChatSummary } from "../../apis/chat";
import ChatSummaryModal from "./ChatSummaryModal";

/**
 * ChatHeader Component - Displays chat header with customer info and AI toggle
 */
const ChatHeader = ({ selectedChat, isWindowOpen, chatAiEnabled, aiToggleLoading, onToggleChatAi }) => {
  const [showSummaryDropdown, setShowSummaryDropdown] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [messageCount, setMessageCount] = useState(50);

  const messageCountOptions = [10, 25, 50, 100, 200, 500];

  const handleGenerateSummary = async () => {
    if (!selectedChat || generatingSummary) return;

    setGeneratingSummary(true);
    setShowSummaryDropdown(false);

    try {
      const response = await generateChatSummary(
        selectedChat.customer_phone,
        selectedChat.phone_number_id,
        messageCount
      );

      if (response.data.success) {
        setSummaryData(response.data.data);
        setShowSummaryModal(true);
      } else {
        alert("Failed to generate summary. Please try again.");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      alert("Failed to generate summary. Please try again.");
    } finally {
      setGeneratingSummary(false);
    }
  };

  return (
    <>
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={18} className="text-primary" />
            </div>
            {!isWindowOpen && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {selectedChat.name}
            </h3>
            <p className="text-xs text-gray-500">{selectedChat.phone}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!isWindowOpen && (
            <span className="text-xs text-yellow-600 px-2 py-1 bg-yellow-50 rounded">
              Window Closed
            </span>
          )}
          
          {/* Generate Summary Button */}
          <div className="relative">
            <button
              onClick={() => setShowSummaryDropdown(!showSummaryDropdown)}
              disabled={generatingSummary}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Generate chat summary"
            >
              {generatingSummary ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FileText size={14} />
                  <span>Generate Summary</span>
                  <ChevronDown size={12} />
                </>
              )}
            </button>

            {/* Dropdown for message count */}
            {showSummaryDropdown && !generatingSummary && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
                <div className="p-2 border-b border-gray-200">
                  <label className="text-xs font-medium text-gray-700 block mb-1">
                    Last Messages:
                  </label>
                  <select
                    value={messageCount}
                    onChange={(e) => setMessageCount(parseInt(e.target.value, 10))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary/40 focus:border-primary"
                  >
                    {messageCountOptions.map((count) => (
                      <option key={count} value={count}>
                        {count} messages
                      </option>
                    ))}
                  </select>
                </div>
                <div className="p-2">
                  <button
                    onClick={handleGenerateSummary}
                    className="w-full px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                  >
                    Generate Summary
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Toggle Button */}
          <button
            onClick={onToggleChatAi}
            disabled={aiToggleLoading}
            className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              chatAiEnabled
                ? "bg-primary/10 text-primary hover:bg-primary/20"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={chatAiEnabled ? "AI Enabled - Click to disable for this chat" : "AI Disabled - Click to enable for this chat"}
          >
            {aiToggleLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Bot size={14} className={chatAiEnabled ? "text-primary" : "text-gray-500"} />
            )}
            <span>{chatAiEnabled ? "AI On" : "AI Off"}</span>
          </button>
          <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
            <MoreVertical size={16} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Summary Modal */}
      {showSummaryModal && summaryData && (
        <ChatSummaryModal
          summaryData={summaryData}
          onClose={() => {
            setShowSummaryModal(false);
            setSummaryData(null);
          }}
        />
      )}

      {/* Click outside to close dropdown */}
      {showSummaryDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowSummaryDropdown(false)}
        />
      )}
    </>
  );
};

export default ChatHeader;

